import React, { useEffect, useState } from "react";
import { api, fmtMoneyShort } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, MetricCard, SectionLabel, Pill, Labeled } from "@/components/common";
import { Donut } from "@/components/charts";
import { PLATFORM_COLORS } from "@/components/charts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Instagram, Facebook, Youtube, Music2, AtSign } from "lucide-react";

const PLATFORMS = ["Instagram", "Facebook", "TikTok", "X", "YouTube"];
const ICON = { Instagram, Facebook, TikTok: Music2, X: AtSign, YouTube: Youtube };
const acctEmpty = { platform: "Instagram", handle: "", followers: 0, views: 0, reach: 0, engagements: 0 };

export default function Social() {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(acctEmpty);

  const load = () => api.get("/social/summary").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connected = new Set((d?.accounts || []).map((a) => a.platform));
  const save = async () => {
    const existing = (d?.accounts || []).find((a) => a.platform === form.platform);
    const payload = { ...form, followers: +form.followers || 0, views: +form.views || 0, reach: +form.reach || 0, engagements: +form.engagements || 0 };
    if (existing) await api.put(`/social_accounts/${existing.id}`, payload); else await api.post("/social_accounts", payload);
    toast.success("Saved"); setDlg(false); load();
  };

  if (!d) return <Loading label="Loading social intelligence…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Social Intelligence" subtitle="Is our influence growing? What content is working?" testId="social-header"
        action={<button data-testid="social-add-btn" onClick={() => { setForm(acctEmpty); setDlg(true); }} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><Plus size={16} /> Update Account</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Total Audience" value={fmtMoneyShort(d.total_audience).replace("$", "")} testId="social-audience" />
        <MetricCard label="Total Reach" value={fmtMoneyShort(d.total_reach).replace("$", "")} />
        <MetricCard label="Total Views" value={fmtMoneyShort(d.total_views).replace("$", "")} />
        <MetricCard label="Total Engagement" value={fmtMoneyShort(d.total_engagement).replace("$", "")} accent="#10B981" />
      </div>

      <div>
        <SectionLabel>Platforms</SectionLabel>
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {PLATFORMS.map((p) => {
            const Icon = ICON[p]; const a = (d.accounts || []).find((x) => x.platform === p);
            return (
              <div key={p} data-testid={`social-platform-${p}`} className="bg-surface border border-edge rounded-xl p-4">
                <div className="flex items-center gap-2"><Icon size={16} style={{ color: PLATFORM_COLORS[p] }} /><span className="font-semibold text-sm">{p}</span></div>
                {a ? (
                  <>
                    <div className="font-num text-2xl mt-2">{(a.followers || 0).toLocaleString()}</div>
                    <div className="label-caps text-[0.55rem]">followers</div>
                  </>
                ) : (
                  <button onClick={() => { setForm({ ...acctEmpty, platform: p }); setDlg(true); }} className="mt-3 text-xs text-gold font-semibold">Connect account</button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface border border-edge rounded-xl p-5">
          <SectionLabel>Audience Share</SectionLabel>
          {d.share.length === 0 ? <EmptyState title="No social data yet." hint="Connect an account to see where attention comes from." /> : (
            <>
              <Donut data={d.share} />
              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                {d.share.map((s) => <Pill key={s.platform} color={PLATFORM_COLORS[s.platform] || "#A3A3A3"}>{s.platform}</Pill>)}
              </div>
            </>
          )}
        </div>
        <div className="bg-surface border border-edge rounded-xl p-5">
          <SectionLabel>What Is Working — Top Posts</SectionLabel>
          {d.top_posts.length === 0 ? <EmptyState title="No posts tracked yet." hint="Add a high-performing post to remember what to create more of." /> : (
            <div className="space-y-2">
              {d.top_posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface2">
                  <div className="min-w-0"><p className="text-sm truncate">{p.title || p.platform}</p><span className="label-caps text-[0.55rem]">{p.platform}</span></div>
                  <span className="font-num text-sm text-gold">{(p.views || 0).toLocaleString()} views</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-[#5a5a5a]">Live API auto-sync (Instagram, TikTok, YouTube, X, Facebook) arrives in a later phase. Metrics shown are values you enter — never fabricated.</p>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Update Account Metrics</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Labeled label="Platform"><select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}</select></Labeled>
            <Labeled label="Handle"><Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="bg-ink border-edge" placeholder="@szr" /></Labeled>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Followers"><Input type="number" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Views"><Input type="number" value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Reach"><Input type="number" value={form.reach} onChange={(e) => setForm({ ...form, reach: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Engagements"><Input type="number" value={form.engagements} onChange={(e) => setForm({ ...form, engagements: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <button data-testid="social-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
